<template>
  <div class="pb-8">
    <div v-if="loading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-2 sm:space-y-0">
        <div class="space-x-6 flex items-center">
          <p class="inline title">Watchlist</p>
        </div>
        <div class="flex items-center space-x-2">
          <el-button
            v-if="!errorLoading"
            :loading="exporting"
            type="plain"
            class="border-brand-gray-blue border-2 px-4 rounded-md"
            @click="exportGroup"
          >
            <span class="text-xs text-brand-blue font-semibold">Export Customers</span>
          </el-button>

          <CompanyHealthFilter :filters="filters" @update="handleFilterUpdate" />
        </div>
      </div>
      <div v-if="errorLoading" class="h-52 relative">
        <div class="text-center absolute h-52 flex items-center justify-center inset-0">
          <el-tooltip effect="dark" placement="bottom-start" popper-class="brand-tooltip">
            <div slot="content">
              <span> This should be finished within 24-48 hours.</span>
            </div>
            <p class="px-4 py-2 text-brand-blue bg-brand-medium-blue bg-opacity-30 text-sm inline-block rounded-lg font-bold mb-1">
              <span>We're busy training our models on your data </span>
            </p>
          </el-tooltip>
        </div>
      </div>
      <div v-else>
        <div class="mt-4">
          <div class="flex space-x-8 text-sm">
            <NavTab
              :active="selectedRisk === 'high'"
              :label="`High Risk (${healthMetrics.high.count} Subscriptions)`"
              value="high"
              @select="selectedRisk = $event"
            />
            <NavTab
              :active="selectedRisk === 'medium'"
              :label="`Medium Risk (${healthMetrics.medium.count} Subscriptions)`"
              value="medium"
              @select="selectedRisk = $event"
            />
            <NavTab
              :active="selectedRisk === 'low'"
              :label="`Low Risk (${healthMetrics.low.count} Subscriptions)`"
              value="low"
              @select="selectedRisk = $event"
            />
          </div>
        </div>
        <div class="mt-8 -ml-2 border-t border-brand-sand">
          <table class="min-w-full divide-y divide-brand-sand">
            <thead>
              <tr class="text-brand-brown text-sm font-semibold">
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left sm:pl-0">Customer</th>
                <th scope="col" class="px-3 py-3.5 text-center">Subscription Status</th>
                <th scope="col" class="px-3 py-3.5 text-center">Subscription Plan</th>
                <th scope="col" class="px-3 py-3.5 text-center">Latest Invoice</th>
                <th scope="col" class="px-3 py-3.5 text-center">Payment Source</th>
                <th scope="col" class="px-3 py-3.5 text-center">Health Trend</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-brand-sand bg-white">
              <template v-for="subscription in displayedSubscriptions">
                <HealthWatchlistRow :row="subscription" :currentGroup="currentGroup" :key="subscription._id" />
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatService from '@/api/stats.service';
import NavTab from '@/components/UI/NavTab.vue';
import { riskGroups } from '@/store/shared';
import { descending } from 'd3-array';
import { EXPORT_DATA } from '@/store/action-types';
import HealthWatchlistRow from './HealthWatchlistRow.vue';
import CompanyHealthFilter from './CompanyHealthFilter.vue';

export default {
  name: 'HealthWatchlist',
  components: {
    NavTab,
    HealthWatchlistRow,
    CompanyHealthFilter,
  },
  data() {
    return {
      loading: false,
      lowRiskCustomers: [],
      mediumRiskCustomers: [],
      highRiskCustomers: [],
      selectedRisk: 'high',
      filters: {},
      appliedFilters: {},
      riskGroups,
      companyHealth: null,
      exporting: false,
      errorLoading: false,
      healthMetrics: {
        low: { count: 0 },
        medium: { count: 0 },
        high: { count: 0 },
      },
    };
  },
  async created() {
    this.loading = true;

    try {
      await this.loadSubscriptionHealths();
      await this.loadCompanyHealth();
    } catch (e) {
      console.log(e);
      this.$message({
        message: 'Error loading customer health dashboard',
        type: 'error',
      });
      this.errorLoading = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    displayedSubscriptions() {
      if (this.selectedRisk === 'low') {
        return this.lowRiskCustomers;
      }
      if (this.selectedRisk === 'medium') {
        return this.mediumRiskCustomers;
      }
      return this.highRiskCustomers;
    },
    currentGroup() {
      return riskGroups.find((group) => group.key === this.selectedRisk);
    },
  },
  methods: {
    async loadCompanyHealth() {
      const companyHealth = await StatService.getCompanyHealth();
      this.companyHealth = companyHealth.latestHealth;
      this.filters = companyHealth.filters;
    },
    async loadSubscriptionHealths() {
      const [lowRiskCustomers, mediumRiskCustomers, highRiskCustomers] = await Promise.all([
        StatService.listSubscriptionHealths({ risk: 'low', ...this.appliedFilters }),
        StatService.listSubscriptionHealths({ risk: 'medium', ...this.appliedFilters }),
        StatService.listSubscriptionHealths({ risk: 'high', ...this.appliedFilters }),
      ]);

      this.lowRiskCustomers = lowRiskCustomers.data;
      this.mediumRiskCustomers = mediumRiskCustomers.data;
      this.highRiskCustomers = highRiskCustomers.data;

      this.healthMetrics.low.count = lowRiskCustomers.totalCount;
      this.healthMetrics.medium.count = mediumRiskCustomers.totalCount;
      this.healthMetrics.high.count = highRiskCustomers.totalCount;

      if (lowRiskCustomers.isPendingProcessing || mediumRiskCustomers.isPendingProcessing || highRiskCustomers.isPendingProcessing) {
        this.errorLoading = true;
      }
    },
    handleFilterUpdate(updatedFilters) {
      this.appliedFilters = updatedFilters;
      this.loadSubscriptionHealths();
    },
    async exportGroup() {
      if (this.exporting) {
        return;
      }
      try {
        this.exporting = true;
        const data = await StatService.exportSubscriptionHealths({ risk: this.selectedRisk, ...this.appliedFilters });
        this.$store.dispatch(EXPORT_DATA, {
          data,
          name: `${this.currentGroup.label} Customers`,
          csv: true,
        });
      } catch (e) {
        console.log(e);
        this.$message({
          message: 'Error exporting customer health',
          type: 'error',
        });
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>
